import { getAccount, getAccountWebview } from "library/api/api";
import {
  DeclineCode,
  getPaymentStatus,
  getPaymentStatusWebview,
  PostPaymentData,
  postPayments,
  postPaymentsWebview,
} from "library/api/payments";
import {
  DebitCardAction,
  DebitCardActionType,
  DebitCardPaymentReducer,
  DebitCardPaymentState,
  initialState,
} from "library/hooks/useDebitCardPayment/DebitCardPaymentReducer";
import { QuickStreamResponseCodes } from "library/hooks/useDebitCardPayment/QuickStreamResponseCodes";
import { TokenError } from "library/hooks/useQuickStream/TokenError";
import { useQuickStream } from "library/hooks/useQuickStream/useQuickStream";
import { useEffect, useReducer } from "react";
import { v4 as uuid } from "uuid";

interface DebitCardPaymentParams {
  accountId: string;
  region: "AU" | "NZ";
  amount: number;
  authToken?: string;
  supplierBusinessCode: string;
  inputStyle?: QuickstreamAPI.StyleMap;
}

const getData = (state: DebitCardPaymentState) => {
  if (state.receiptNumber) {
    return {
      receiptNumber: state.receiptNumber,
      cardNumber: state.cardNumber,
      cardScheme: state.cardScheme,
    };
  }
  return undefined;
};

interface SubmitPaymentParams {
  savedInstrument?: {
    id: string;
    cardNumber: string;
    cardScheme: string;
  };
  saveCard?: boolean;
}

const regionToCurrencyMap = {
  AU: "AUD",
  NZ: "NZD",
};

const getBusinessRuleDeclineAction = (
  declineCode: DeclineCode,
  region: "AU" | "NZ",
): DebitCardAction => {
  if (declineCode === "PAYMENT_AMOUNT_GREATER_THAN_CREDIT_BALANCE") {
    return {
      type: DebitCardActionType.EXCEED_CREDIT_LIMIT,
    };
  }
  if (declineCode === "CARD_PRODUCT_REGION_NOT_MATCH") {
    return {
      type: DebitCardActionType.CARD_DOES_NOT_MATCH_REGION,
      region,
    };
  }
  return {
    type: DebitCardActionType.PAYMENT_STATUS_FAILURE,
  };
};

export const useDebitCardPayment = ({
  accountId,
  amount,
  authToken,
  inputStyle,
  region,
  supplierBusinessCode,
}: DebitCardPaymentParams) => {
  const {
    getToken,
    status: quickStreamStatus,
    quickStreamRef,
  } = useQuickStream({ supplierBusinessCode, region, inputStyle });
  const [state, dispatch] = useReducer(DebitCardPaymentReducer, initialState);
  const { statusUrl } = state;

  useEffect(() => {
    if (quickStreamStatus === "READY") {
      dispatch({ type: DebitCardActionType.QUICK_STREAM_READY });
    }
  }, [quickStreamStatus]);

  const submitPayment = async ({
    savedInstrument,
    saveCard = false,
  }: SubmitPaymentParams) => {
    dispatch({
      type: DebitCardActionType.SUBMIT_PAYMENT,
    });

    try {
      const { data: account } = authToken
        ? await getAccountWebview(accountId, authToken)
        : await getAccount(accountId);

      const payWithDebitCardLink = account.links.find(
        ({ rel }) => rel === "debit_card",
      );

      const commonData = {
        amount,
        payment_type: "SCHEME_CARD",
        currency: regionToCurrencyMap[region],
      };

      let body: PostPaymentData;

      if (savedInstrument) {
        body = {
          ...commonData,
          instrument_id: savedInstrument.id,
        };
        dispatch({
          type: DebitCardActionType.SAVED_PAYMENT_INSTRUMENT_RECEIVED,
          cardNumber: savedInstrument.cardNumber,
          cardScheme: savedInstrument.cardScheme,
        });
      } else {
        const singleUseToken = await getToken();

        dispatch({
          type: DebitCardActionType.TOKEN_RECEIVED,
          cardNumber: singleUseToken.lastFour,
          cardScheme: singleUseToken.cardScheme,
        });

        body = {
          ...commonData,
          single_use_token: singleUseToken.token,
          masked_card_number: singleUseToken.maskedCardNumber,
          save_instrument: saveCard,
        };
      }

      const response = authToken
        ? await postPaymentsWebview(
            payWithDebitCardLink.href,
            body,
            uuid(),
            authToken,
          )
        : await postPayments(payWithDebitCardLink.href, body, uuid());

      const { data } = response;
      dispatch({
        type: DebitCardActionType.PAYMENT_POST_SUCCESS,
        statusUrl: data.status_url,
      });
    } catch (err) {
      // token errors are displayed and handled by quickstream so don't need to add error
      if (err instanceof TokenError) {
        dispatch({ type: DebitCardActionType.TOKEN_ERROR });
      } else if (err instanceof Error) {
        dispatch({
          type: DebitCardActionType.PAYMENT_POST_FAILURE,
          errorMessage: err.message,
        });
      } else {
        dispatch({
          type: DebitCardActionType.PAYMENT_POST_FAILURE,
          errorMessage: "Something went wrong",
        });
      }
    }
  };

  useEffect(() => {
    if (!statusUrl) {
      return () => {};
    }

    // poll the status of the payment
    const interval = setInterval(async () => {
      try {
        // check status of the payment
        const res = authToken
          ? await getPaymentStatusWebview(statusUrl, uuid(), authToken)
          : await getPaymentStatus(statusUrl, uuid());

        // the payment is pending so check again in the next poll call
        if (res.data.status === "PENDING") {
          return;
        }

        // payment has been processed so no longer poll
        clearInterval(interval);

        // react to the status of the payment
        switch (res.data.status) {
          case "APPROVED":
            dispatch({
              type: DebitCardActionType.PAYMENT_APPROVED,
              receiptNumber: res.data.pg_receipt_number,
            });
            break;
          case "BUSINESS_RULES_DECLINED":
            dispatch(
              getBusinessRuleDeclineAction(res.data.decline_code, region),
            );
            break;
          case "DECLINED":
            dispatch({
              type:
                res.data.pg_response_code ===
                QuickStreamResponseCodes.INSUFFICIENT_FUNDS
                  ? DebitCardActionType.INSUFFICIENT_FUNDS
                  : DebitCardActionType.PAYMENT_DECLINED,
            });
            break;
          default:
            dispatch({
              type: DebitCardActionType.PAYMENT_STATUS_FAILURE,
            });
        }
      } catch (err) {
        clearInterval(interval);
        dispatch({
          type: DebitCardActionType.PAYMENT_STATUS_FAILURE,
        });
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [statusUrl, authToken, region]);

  return {
    quickStreamRef,
    submitPayment,
    error: state.error,
    data: getData(state),
    status: state.status,
  };
};
